import { i18n } from "src/boot/i18n"
import { WorkOrderPriorityDocument } from "src/db/collectionDefinitions/csi/workOrderPriority"
import { getDB } from "src/db/databases"
import { useCacheStore } from "src/stores/cacheStore"
import { useLocalUserSettingsStore } from "src/stores/localUserSettingsStore"
import { useUserStore } from "src/stores/userStore"
import { Log } from "src/util/classes/log"
import { MILLISECONDS_PER_DAY } from "src/util/constants/temporal"
import { uploadImages } from "src/util/functions/attachments"
import {
  closeWorkOrderCompletedDialog,
  closeWorkOrderNotCompletedDialog,
} from "src/util/functions/dialogs"
import { getIssuesOnItemForCorrectiveWorkOrder } from "src/util/functions/issues"
import { getItemDataByIds } from "src/util/functions/item"
import {
  showErrorNotification,
  showSuccessNotification,
  showWarningNotification,
  simpleUndoNotification,
} from "src/util/functions/notifications"
import {
  getCorrectiveWorkOnItemListForWorkOrder,
  getRegularWorkOnItemListForWorkOrder,
} from "src/util/functions/performedWork"
import { getWorkDomainWorkspaceData } from "src/util/functions/workDomain"
import { getWorkOrderPriorityData } from "src/util/functions/workOrderPriority"
import {
  getWorkOrderCorrectiveItem,
  getWorkOrderCorrectiveItemComment,
  getWorkOrderCorrectiveItemCommentList,
  getWorkOrderRegularItem,
  getWorkOrderRegularItemComment,
  getWorkOrderRegularItemCommentList,
  patchWorkOrderCorrectiveItem,
  patchWorkOrderRegularItem,
  postWorkOrderCorrectiveItemComment,
  postWorkOrderRegularItemComment,
} from "src/util/http/csi/workOrders"
import { ProgressIndicatorState } from "src/util/types/components/sfxProgressIndicator"
import {
  WorkOrderStateOptions,
  WorkOrderStateTableRowElement,
} from "src/util/types/components/sfxTable"
import { AttachmentUpload } from "src/util/types/dataModels/attachment"
import {
  PERMISSION_ACCESS_CREATE,
  PERMISSION_ACCESS_UPDATE,
  PERMISSION_WORK_ORDERS,
} from "src/util/types/dataModels/user"
import { WorkDomainWorkspace } from "src/util/types/dataModels/workDomain"
import {
  WorkOrderBase,
  WorkOrderCardData,
  WorkOrderCorrectiveItem,
  WorkOrderRegularItem,
  WorkOrderTableData,
} from "src/util/types/dataModels/workOrder"
import {
  WorkOrderCorrectiveItemComment,
  WorkOrderCorrectiveItemCommentPayload,
  WorkOrderRegularItemComment,
  WorkOrderRegularItemCommentPayload,
} from "src/util/types/dataModels/workOrderComment"
import { WorkOrderPriority } from "src/util/types/dataModels/workOrderPriority"

export function userHasPermissionToCreateWorkOrder(): boolean {
  const userStore = useUserStore()

  return userStore.checkUserPermission(PERMISSION_WORK_ORDERS, PERMISSION_ACCESS_CREATE)
}

export function userHasPermissionToUpdateWorkOrder(workDomain: string): boolean {
  const userStore = useUserStore()

  return userStore.checkUserPermission(PERMISSION_WORK_ORDERS, PERMISSION_ACCESS_UPDATE, {
    workDomainUUID: workDomain,
  })
}

export async function getWorkOrderCorrectiveItemData(
  workOrderCorrectiveItemId: string
): Promise<WorkOrderCorrectiveItem | undefined> {
  try {
    const cacheStore = useCacheStore()

    const workOrderInCache = cacheStore.work_order_corrective_item.get(workOrderCorrectiveItemId)

    if (workOrderInCache) {
      return workOrderInCache
    } else {
      const db = await getDB()
      const workOrderCorrectiveItemInDb = await db.work_order_corrective_item
        .findOne(workOrderCorrectiveItemId)
        .exec()

      if (workOrderCorrectiveItemInDb) {
        const workOrderMutable = workOrderCorrectiveItemInDb.toMutableJSON()
        cacheStore.work_order_corrective_item.set(workOrderMutable.id, workOrderMutable)
        return workOrderMutable
      } else {
        return await getWorkOrderCorrectiveItem({}, workOrderCorrectiveItemId)
      }
    }
  } catch (error) {
    Log.error({
      message: "An error occurred while fetching Work Order Corrective Item data",
      error,
    })
  }
}

export async function getWorkOrderCorrectiveItemCommentData(
  commentId: string
): Promise<WorkOrderCorrectiveItemComment | undefined> {
  try {
    const cacheStore = useCacheStore()
    const workOrderCommentInCache = cacheStore.work_order_corrective_item_comments.get(commentId)

    if (workOrderCommentInCache) {
      return workOrderCommentInCache
    } else {
      const db = await getDB()
      const commentInDb = await db.work_order_corrective_comments.findOne(commentId).exec()

      if (commentInDb) {
        const mutableComment = commentInDb.toMutableJSON()
        cacheStore.work_order_corrective_item_comments.set(mutableComment.id, mutableComment)
        return mutableComment
      } else {
        return await getWorkOrderCorrectiveItemComment({}, commentId)
      }
    }
  } catch (error) {
    Log.error({
      message:
        "An error occurred while fetching getWorkOrderCorrectiveItemComment data for a given commentId",
      error,
      data: { commentId: commentId },
    })
  }
}

export async function getWorkOrderCorrectiveItemCommentListForWorkOrderCorrectiveItem(
  workOrderId: string
): Promise<WorkOrderCorrectiveItemComment[] | undefined> {
  try {
    const cacheStore = useCacheStore()
    let commentList: WorkOrderCorrectiveItemComment[] = []

    if (cacheStore.work_order_corrective_item_comments.size > 0) {
      for (const commentInCache of cacheStore.work_order_corrective_item_comments.values()) {
        if (commentInCache.work_order === workOrderId) {
          commentList.push(commentInCache)
        }
      }
    } else {
      const db = await getDB()
      const commentsInDb = await db.work_order_corrective_comments
        .find({ selector: { work_order: workOrderId } })
        .exec()

      if (commentsInDb.length > 0) {
        for (const comment of commentsInDb) {
          const commentMutable = comment.toMutableJSON()
          cacheStore.work_order_corrective_item_comments.set(commentMutable.id, commentMutable)
          commentList.push(comment)
        }
      } else {
        const commentsInServer = await getWorkOrderCorrectiveItemCommentList({
          mainParams: undefined,
          filterParams: { work_order: workOrderId },
        })
        commentList = commentsInServer.results
      }
    }
    return commentList.sort((a, b) => b.created_dt.localeCompare(a.created_dt))
  } catch (error) {
    Log.error({
      message:
        "An error occurred while fetching WorkOrderCorrectiveItemComment list for a given WorkOrderCorrective ID",
      error,
      data: { workOrderId: workOrderId },
    })
  }
}

export async function saveWorkOrderCorrectiveItemComment(
  workOrderId: string,
  commentText: string,
  commentImages?: AttachmentUpload[]
): Promise<WorkOrderCorrectiveItemComment | undefined> {
  const userStore = useUserStore()
  const cacheStore = useCacheStore()

  try {
    const comment: WorkOrderCorrectiveItemCommentPayload = {
      text: commentText,
      user: userStore.id,
      work_order: workOrderId,
    }

    const commentInServer = await postWorkOrderCorrectiveItemComment({}, comment)

    if (commentInServer) {
      if (commentImages && commentImages.length > 0) {
        commentInServer.attachments =
          (await uploadImages(
            "work_order_corrective_item_comment",
            commentInServer.id,
            commentImages
          )) || []
      }

      const db = await getDB()
      await db.work_order_corrective_comments.insert(commentInServer)
      // Also add the comment to the cache layer
      cacheStore.work_order_corrective_item_comments.set(commentInServer.id, commentInServer)

      return commentInServer
    }
  } catch (error) {
    const { t } = i18n.global
    showErrorNotification(t("sfxErrorMsg.error_adding_comment"))

    Log.error({
      message: "An error occurred while saving the WorkOrderCorrectiveItem comment/attachments",
      error,
      data: { workOrderId: workOrderId },
    })
  }
}

export async function getWorkOrderRegularItemData(
  workOrderRegularItemId: string
): Promise<WorkOrderRegularItem | undefined> {
  try {
    const cacheStore = useCacheStore()

    const workOrderInCache = cacheStore.work_order_regular_item.get(workOrderRegularItemId)

    if (workOrderInCache) {
      return workOrderInCache
    } else {
      const db = await getDB()
      const workOrderRegularItemInDb = await db.work_order_regular_item
        .findOne(workOrderRegularItemId)
        .exec()

      if (workOrderRegularItemInDb) {
        const workOrderMutable = workOrderRegularItemInDb.toMutableJSON()
        cacheStore.work_order_regular_item.set(workOrderMutable.id, workOrderMutable)
        return workOrderMutable
      } else {
        return await getWorkOrderRegularItem({}, workOrderRegularItemId)
      }
    }
  } catch (error) {
    Log.error({
      message: "An error occurred while fetching Work Order Regular Item data",
      error,
    })
  }
}

export async function getWorkOrderRegularItemCommentData(
  commentId: string
): Promise<WorkOrderRegularItemComment | undefined> {
  try {
    const cacheStore = useCacheStore()
    const workOrderCommentInCache = cacheStore.work_order_regular_item_comments.get(commentId)

    if (workOrderCommentInCache) {
      return workOrderCommentInCache
    } else {
      const db = await getDB()
      const commentInDb = await db.work_order_regular_comments.findOne(commentId).exec()

      if (commentInDb) {
        const mutableComment = commentInDb.toMutableJSON()
        cacheStore.work_order_regular_item_comments.set(mutableComment.id, mutableComment)
        return mutableComment
      } else {
        return await getWorkOrderRegularItemComment({}, commentId)
      }
    }
  } catch (error) {
    Log.error({
      message:
        "An error occurred while fetching getWorkOrderRegularItemComment data for a given commentId",
      error,
      data: { commentId: commentId },
    })
  }
}

export async function getWorkOrderRegularItemCommentListForWorkOrderRegularItem(
  workOrderId: string
): Promise<WorkOrderRegularItemComment[] | undefined> {
  try {
    const cacheStore = useCacheStore()
    let commentList: WorkOrderRegularItemComment[] = []

    if (cacheStore.work_order_regular_item_comments.size > 0) {
      for (const commentInCache of cacheStore.work_order_regular_item_comments.values()) {
        if (commentInCache.work_order === workOrderId) {
          commentList.push(commentInCache)
        }
      }
    } else {
      const db = await getDB()
      const commentsInDb = await db.work_order_regular_comments
        .find({ selector: { work_order: workOrderId } })
        .exec()

      if (commentsInDb.length > 0) {
        for (const comment of commentsInDb) {
          const commentMutable = comment.toMutableJSON()
          cacheStore.work_order_regular_item_comments.set(commentMutable.id, commentMutable)
          commentList.push(comment)
        }
      } else {
        const commentsInServer = await getWorkOrderRegularItemCommentList({
          mainParams: undefined,
          filterParams: { work_order: workOrderId },
        })
        commentList = commentsInServer.results
      }
    }
    return commentList.sort((a, b) => b.created_dt.localeCompare(a.created_dt))
  } catch (error) {
    Log.error({
      message:
        "An error occurred while fetching WorkOrderRegularItemComment list for a given WorkOrderRegular ID",
      error,
      data: { workOrderId: workOrderId },
    })
  }
}

export async function saveWorkOrderRegularItemComment(
  workOrderId: string,
  commentText: string,
  commentImages?: AttachmentUpload[]
): Promise<WorkOrderRegularItemComment | undefined> {
  const userStore = useUserStore()
  const cacheStore = useCacheStore()

  try {
    const comment: WorkOrderRegularItemCommentPayload = {
      text: commentText,
      user: userStore.id,
      work_order: workOrderId,
    }

    const commentInServer = await postWorkOrderRegularItemComment({}, comment)

    if (commentInServer) {
      if (commentImages && commentImages.length > 0) {
        commentInServer.attachments =
          (await uploadImages(
            "work_order_regular_item_comment",
            commentInServer.id,
            commentImages
          )) || []
      }

      const db = await getDB()
      await db.work_order_regular_comments.insert(commentInServer)
      // Also save the comment in the cache layer.
      cacheStore.work_order_regular_item_comments.set(commentInServer.id, commentInServer)

      return commentInServer
    }
  } catch (error) {
    const { t } = i18n.global
    showErrorNotification(t("sfxErrorMsg.error_adding_comment"))

    Log.error({
      message: "An error occurred while saving the WorkOrderRegularItem comment/attachments",
      error,
      data: { workOrderId: workOrderId },
    })
  }
}

/**
 * Returns the appropriate label based on provided data.
 *
 * The different states have precedence over one another, which is:
 * 1. Completed + Closed
 * 2. Closed
 * 3. Draft
 * 4. Completed
 * 5. In progress
 * 6. Open
 * 7. Planned
 */
export function getProgressIndicatorStateLabel(
  nonRejectedCount: number,
  totalCount: number,
  workOrder: WorkOrderCorrectiveItem | WorkOrderRegularItem
): ProgressIndicatorState {
  if (workOrder.concluded_dt !== null) {
    return totalCount > 0 && nonRejectedCount === totalCount ? "complete_closed" : "closed"
  } else if (totalCount === 0) return "draft"
  else if (nonRejectedCount === totalCount) return "complete"
  else if (nonRejectedCount > 0) return "in_progress"
  else if (Date.now() > new Date(workOrder.start_dt).getTime()) return "open"
  else if (Date.now() < new Date(workOrder.start_dt).getTime()) return "planned"
  else return "unknown"
}

function getWorkOrderState(
  workOrder: WorkOrderCorrectiveItem | WorkOrderRegularItem,
  completedWorkCount: number,
  entityCount: number
): WorkOrderStateTableRowElement {
  if (entityCount === 0 && workOrder.concluded_dt === null) {
    return "draft"
  } else {
    const workCompleted = entityCount > 0 && entityCount === completedWorkCount

    if (workCompleted && workOrder.concluded_dt !== null) {
      return "complete_closed"
    } else if (workOrder.concluded_dt !== null) {
      return "closed"
      // Checking workOrder.workOrderData.concluded_dt === null is redundant
    } else if (workCompleted) {
      return "complete"
    }
  }

  const currentDT = Date.now()
  const startDT = new Date(workOrder.start_dt).getTime()

  const overdueOrBehindState = getDeadlineState(workOrder, completedWorkCount, entityCount)
  if (overdueOrBehindState) return overdueOrBehindState

  if (workOrder.assigned_users.length === 0) {
    // TODO: 'resource' logic will be updated after https://gitlab.com/safeex/safeex-app-team/safeex-cloud-app/-/issues/737
    return "resource"
  } else if (completedWorkCount > 0) {
    return "in_progress"
    // Checking workOrder.workOrderData.concluded_dt === null is redundant
  } else if (startDT < currentDT) {
    return "open"
    // Checking workOrder.workOrderData.concluded_dt === null is redundant
  } else if (currentDT < startDT) {
    return "planned"
  } else {
    return "unknown"
  }
}

/**
 * Computes if a work order is 'overdue', 'behind' or none of those two.
 */
export function getDeadlineState(
  workOrder: WorkOrderCorrectiveItem | WorkOrderRegularItem,
  completedWorkCount: number,
  totalWorkCount: number
): undefined | "behind" | "overdue" {
  // `overdue` and `behind` only make sense when there is a deadline
  if (!workOrder.deadline_dt) {
    return
  }
  const current = Date.now()
  const deadline = new Date(workOrder.deadline_dt).getTime()
  if (deadline < current) {
    return "overdue"
  }
  const start = new Date(workOrder.start_dt).getTime()
  if (current < start) {
    // Work order has not started yet.
    return undefined
  }
  // Source: https://www.figma.com/design/v7AuSqfZGPD2XrEvkSkPut/APEX%3A-Work-Management?node-id=678-49414&m=dev
  const daysSpent = (current - start) / MILLISECONDS_PER_DAY
  const daysRemaining = (deadline - current) / MILLISECONDS_PER_DAY
  const daysPerWork = completedWorkCount / daysSpent
  const workToDo = totalWorkCount - completedWorkCount
  const daysNeeded = workToDo * daysPerWork

  if (daysNeeded < daysRemaining) return "behind"
}

/**
 * Sort the work order data in the following way:
 *
 * 1. Not Completed
 * 2. Completed
 * 3. Closed
 *
 * Furthermore, each of the 3 categories are sorted internally into:
 *
 * #1 - Split into first with deadline and then without deadline.
 * The former is then sorted by earliest deadline.
 * The latter is sorted based on work order priority.
 *
 * #2 - Sort on work order priority.
 *
 * #3 - Sort on closed date.
 *
 * @param a
 * @param b
 */
export function sortWorkOrderCardData(a: WorkOrderCardData, b: WorkOrderCardData): number {
  if (a.workOrderData.concluded_dt && b.workOrderData.concluded_dt) {
    return sortWorkOrderClosedCardData(a, b)
  }

  const closedStatus = sortWorkOrderClosedStatus(a, b)

  if (closedStatus !== 0) return closedStatus

  return sortWorkOrderNotClosedCardData(a, b)
}

/**
 * Sort the not closed work order data in the following way:
 *
 * 1. Not Completed
 * 2. Completed
 *
 * Furthermore, each of the 2 categories are sorted internally into:
 *
 * #1 - Split into first with deadline and then without deadline.
 * The former is then sorted by earliest deadline.
 * The latter is sorted based on work order priority.
 *
 * #2 - Sort on work order priority.
 *
 * @param a
 * @param b
 */
export function sortWorkOrderNotClosedCardData(a: WorkOrderCardData, b: WorkOrderCardData): number {
  const aTotal = a.isCorrectiveWork ? a.issuesData.length : a.workOrderData.items.length
  const bTotal = b.isCorrectiveWork ? b.issuesData.length : b.workOrderData.items.length

  const aCount = a.performedWorkData.length
  const bCount = b.performedWorkData.length

  const aCompleted = aTotal > 0 && aTotal === aCount
  const bCompleted = bTotal > 0 && bTotal === bCount

  if (aCompleted !== bCompleted) {
    return aCompleted ? 1 : -1
  } else if (aCompleted && bCompleted) {
    return sortWorkOrderPriority(a.priorityData, b.priorityData)
  } else {
    return sortWorkOrderNotCompletedCardData(a, b)
  }
}

/**
 * Sort the not completed work order data in the following way:
 *
 * 1. With Deadline
 * 2. Without Deadline
 *
 * Furthermore, each of the 2 categories are sorted internally into:
 *
 * #1 - Sort by earliest deadline.
 *
 * #2 - Sort on work order priority.
 *
 * @param a
 * @param b
 */
function sortWorkOrderNotCompletedCardData(a: WorkOrderCardData, b: WorkOrderCardData): number {
  if (a.workOrderData.deadline_dt && b.workOrderData.deadline_dt) {
    return sortWorkOrderDeadlineCardData(a, b)
  } else {
    const deadlineStatus = sortWorkOrderDeadlineStatus(a, b)

    if (deadlineStatus !== 0) return deadlineStatus

    return sortWorkOrderPriority(a.priorityData, b.priorityData)
  }
}

function sortWorkOrderClosedStatus(a: WorkOrderCardData, b: WorkOrderCardData): number {
  if (a.workOrderData.concluded_dt === null && b.workOrderData.concluded_dt === null) {
    return 0
  } else return a.workOrderData.concluded_dt === null ? -1 : 1
}

function sortWorkOrderDeadlineStatus(a: WorkOrderCardData, b: WorkOrderCardData): number {
  if (a.workOrderData.deadline_dt === null && b.workOrderData.deadline_dt === null) {
    return 0
  } else {
    return a.workOrderData.deadline_dt === null ? 1 : -1
  }
}

/**
 * Sort work orders based on their deadline dates.
 * This functions assumes that all the work orders have valid deadline date time.
 */
function sortWorkOrderDeadlineCardData(a: WorkOrderCardData, b: WorkOrderCardData): number {
  const now = Date.now()
  const aTime = now - new Date(a.workOrderData.deadline_dt || 0).getTime()
  const bTime = now - new Date(b.workOrderData.deadline_dt || 0).getTime()

  return bTime - aTime
}

/**
 * Sort closed work orders based on their closing dates.
 * This functions assumes that all the work orders have valid conclusion date time.
 */
export function sortWorkOrderClosedCardData(a: WorkOrderCardData, b: WorkOrderCardData): number {
  const aDate = new Date(a.workOrderData.concluded_dt || 0).getTime()
  const bDate = new Date(b.workOrderData.concluded_dt || 0).getTime()

  return bDate - aDate
}

export function sortWorkOrderDeadline(
  a: WorkOrderCardData | WorkOrderTableData,
  b: WorkOrderCardData | WorkOrderTableData,
  dir?: -1 | 1
): number {
  if (a.workOrderData.deadline_dt === null && b.workOrderData.deadline_dt === null) return 0

  if (a.workOrderData.deadline_dt === null) return 1

  if (b.workOrderData.deadline_dt === null) return -1

  const now = Date.now()
  const aTime = now - new Date(a.workOrderData.deadline_dt).getTime()
  const bTime = now - new Date(b.workOrderData.deadline_dt).getTime()

  const timeDifference = bTime - aTime

  return dir ? timeDifference * dir : timeDifference
}

export function sortWorkOrderPriority(
  a: WorkOrderPriority,
  b: WorkOrderPriority,
  dir?: -1 | 1
): number {
  const rankDifference = b.rank - a.rank
  return dir ? rankDifference * dir : rankDifference
}

export async function getWorkOrderCorrectiveCardData(
  workOrder: WorkOrderCorrectiveItem
): Promise<WorkOrderCardData> {
  const priority = await getWorkOrderPriorityData(workOrder.priority)
  const work = await getCorrectiveWorkOnItemListForWorkOrder(workOrder.id)
  const issues = await getIssuesOnItemForCorrectiveWorkOrder(workOrder.id)
  const workDomain = await getWorkDomainWorkspaceData(workOrder.work_domain)
  const performedWork = work?.filter((i) => i.conclusion !== "rejected") || []
  const completedWorkCount = performedWork.map((work) => work.conclusion).length

  return {
    workOrderData: workOrder,
    workOrderState: getWorkOrderState(workOrder, completedWorkCount, issues.length),
    priorityData: priority!,
    isCorrectiveWork: true,
    isItem: true,
    workDomainData: workDomain,
    issuesData: issues,
    performedWorkData: performedWork,
  }
}

export async function getWorkOrderRegularCardData(
  workOrder: WorkOrderRegularItem
): Promise<WorkOrderCardData> {
  const priority = await getWorkOrderPriorityData(workOrder.priority)
  const work = await getRegularWorkOnItemListForWorkOrder(workOrder.id)
  const items = await getItemDataByIds(workOrder.items)
  const workDomain = await getWorkDomainWorkspaceData(workOrder.work_domain)
  const performedWork = work?.filter((i) => i.conclusion !== "rejected") || []
  const completedWorkCount = performedWork.map((work) => work.conclusion).length

  return {
    workOrderData: workOrder,
    workOrderState: getWorkOrderState(workOrder, completedWorkCount, items.length),
    priorityData: priority!,
    isCorrectiveWork: false,
    isItem: true,
    workDomainData: workDomain,
    itemsData: items,
    performedWorkData: performedWork,
  }
}

// This getWorkOrderTableData function is identical to the getWorkOrderCardData function as of this writing.
// The reason for this duplication is to maintain the separation of concerns.
// eslint-disable-next-line sonarjs/no-identical-functions
export async function getWorkOrderCorrectiveTableData(
  workOrder: WorkOrderCorrectiveItem
): Promise<WorkOrderTableData> {
  const priority = await getWorkOrderPriorityData(workOrder.priority)
  const work = await getCorrectiveWorkOnItemListForWorkOrder(workOrder.id)
  const issues = await getIssuesOnItemForCorrectiveWorkOrder(workOrder.id)
  const workDomain = await getWorkDomainWorkspaceData(workOrder.work_domain)
  const performedWork = work?.filter((i) => i.conclusion !== "rejected") || []
  const completedWorkCount = performedWork.map((work) => work.conclusion).length

  return {
    workOrderData: workOrder,
    workOrderState: getWorkOrderState(workOrder, completedWorkCount, issues.length),
    indicator: {
      total: issues.length,
      current: completedWorkCount,
    },
    priorityData: priority!,
    isCorrectiveWork: true,
    isItem: true,
    workDomainData: workDomain,
    issuesData: issues,
    performedWorkData: performedWork,
  }
}

// This getWorkOrderTableData function is identical to the getWorkOrderCardData function as of this writing.
// The reason for this duplication is to maintain the separation of concerns.
// eslint-disable-next-line sonarjs/no-identical-functions
export async function getWorkOrderRegularTableData(
  workOrder: WorkOrderRegularItem
): Promise<WorkOrderTableData> {
  const priority = await getWorkOrderPriorityData(workOrder.priority)
  const work = await getRegularWorkOnItemListForWorkOrder(workOrder.id)
  const items = await getItemDataByIds(workOrder.items)
  const workDomain = await getWorkDomainWorkspaceData(workOrder.work_domain)
  const performedWork = work?.filter((i) => i.conclusion !== "rejected") || []
  const completedWorkCount = performedWork.map((work) => work.conclusion).length

  return {
    workOrderData: workOrder,
    workOrderState: getWorkOrderState(workOrder, completedWorkCount, items.length),
    indicator: {
      total: items.length,
      current: completedWorkCount,
    },
    priorityData: priority!,
    isCorrectiveWork: false,
    isItem: true,
    workDomainData: workDomain,
    itemsData: items,
    performedWorkData: performedWork,
  }
}

export async function getHighestPriorityFromWorkOrderList(
  list: Array<WorkOrderCardData | WorkOrderRegularItem | WorkOrderCorrectiveItem>
): Promise<WorkOrderPriorityDocument | null> {
  const db = await getDB()
  const ids = list.map((workOrder) =>
    "workOrderData" in workOrder ? workOrder.workOrderData.priority : workOrder.priority
  )

  const prioritiesMap = await db.work_order_priority.findByIds(ids).exec()
  if (prioritiesMap.size === 0) return null
  const priorities = Array.from(prioritiesMap.values())

  return priorities.reduce((prev, current) => (prev.rank > current.rank ? prev : current))
}

export async function getOpenWorkOrders(): Promise<
  Array<WorkOrderCorrectiveItem | WorkOrderRegularItem>
> {
  const db = await getDB()
  const corrective = await db.work_order_corrective_item
    .find({
      selector: {
        // Is open, meaning not concluded and started in the past.
        concluded_dt: null,
        // TODO: there is an issue with datetimes not being UTC from the back end.
        // This code assumes UTC, but once it's fixed it should work fine.
        start_dt: { $lt: new Date().toISOString() },
      },
    })
    .exec()
  const regular = await db.work_order_regular_item
    .find({ selector: { concluded_dt: null, start_dt: { $lt: new Date().toISOString() } } })
    .exec()

  return [...corrective, ...regular]
}

export async function getOpenAndAssignedWorkOrders(
  userId: string
): Promise<Array<WorkOrderCorrectiveItem | WorkOrderRegularItem>> {
  const db = await getDB()
  const corrective = await db.work_order_corrective_item
    .find({
      selector: {
        // Is assigned to user
        assigned_users: { $in: [userId] },
        // Is open, meaning not concluded and started in the past.
        concluded_dt: null,
        // TODO: there is an issue with datetimes not being UTC from the back end.
        // This code assumes UTC, but once it's fixed it should work fine.
        start_dt: { $lt: new Date().toISOString() },
      },
    })
    .exec()
  const regular = await db.work_order_regular_item
    .find({
      selector: {
        assigned_users: { $in: [userId] },
        concluded_dt: null,
        start_dt: { $lt: new Date().toISOString() },
      },
    })
    .exec()

  return [...corrective, ...regular]
}

/**
 * Attempts to close a WorkOrder.
 * Depending on if the work order is 'completed' or not, it displays the appropriate dialog.
 * Then it triggers an undo notification, allowing the user to undo their action.
 *
 * If the undo action is taken, then it returns undefined.
 * Else it will attempt to close the work order and submit a comment if the work order is not 'complete'.
 *
 * @param isCorrective
 * @param workOrder
 * @param workOrderState
 * @param workDomain
 */
export async function closeWorkOrder(
  isCorrective: boolean,
  workOrder: WorkOrderBase,
  workOrderState: WorkOrderStateOptions,
  workDomain: WorkDomainWorkspace
): Promise<WorkOrderBase | undefined> {
  const { t } = i18n.global

  if (!userHasPermissionToUpdateWorkOrder(workDomain.id)) {
    showWarningNotification(t("permissionNotifications.no_access"))
    return
  }

  const localUserSettingsStore = useLocalUserSettingsStore()
  let closingComment: string | null = null

  if (workOrderState === "complete") {
    if (!localUserSettingsStore.skipConfirmCloseWorkOrder) {
      const userResponse = await closeWorkOrderCompletedDialog()
      if (userResponse === null) return

      // Update the local user settings
      localUserSettingsStore.skipConfirmCloseWorkOrder = userResponse
    }
  } else {
    // User needs to have CREATE permission to create a work order comment
    if (!userHasPermissionToCreateWorkOrder()) {
      showWarningNotification(t("permissionNotifications.no_access"))
      return
    }

    closingComment = await closeWorkOrderNotCompletedDialog()
    if (!closingComment) return
  }

  const undoMessage =
    workOrderState === "complete"
      ? t("notifications.closed_completed_work_order")
      : t("notifications.closed_work_order")

  return simpleUndoNotification(undoMessage, () =>
    closeWorkOrderOperations(isCorrective, workOrder, closingComment)
  )
}

async function closeWorkOrderOperations(
  isCorrective: boolean,
  workOrder: WorkOrderBase,
  closingComment: string | null
) {
  const userStore = useUserStore()

  workOrder.concluded_dt = new Date().toJSON()
  workOrder.concluded_by_user = userStore.id

  return await (isCorrective
    ? closeWorkOrderCorrectiveOnItem(workOrder, closingComment)
    : closeWorkOrderRegularOnItem(workOrder, closingComment))
}

async function closeWorkOrderCorrectiveOnItem(
  workOrder: WorkOrderBase,
  closingComment: string | null
) {
  const { t } = i18n.global

  try {
    if (closingComment) {
      await saveWorkOrderCorrectiveItemComment(workOrder.id, closingComment)
    }

    const closedWorkOrder = await patchWorkOrderCorrectiveItem({}, workOrder.id, workOrder)

    if (closedWorkOrder) {
      const db = await getDB()
      await db.work_order_corrective_item.upsert(closedWorkOrder)

      showSuccessNotification(t("notifications.work_order_corrective_closed_success"))
      return closedWorkOrder
    }
  } catch (error) {
    showErrorNotification(t("sfxErrorMsg.error_closing_work_order_corrective"))
    Log.error({
      message: "An error occurred during corrective work order closing data operations",
      error,
    })
  }
}

async function closeWorkOrderRegularOnItem(
  workOrder: WorkOrderBase,
  closingComment: string | null
) {
  const { t } = i18n.global

  try {
    if (closingComment) {
      await saveWorkOrderRegularItemComment(workOrder.id, closingComment)
    }

    const closedWorkOrder = await patchWorkOrderRegularItem({}, workOrder.id, workOrder)

    if (closedWorkOrder) {
      const db = await getDB()
      await db.work_order_regular_item.upsert(closedWorkOrder)

      showSuccessNotification(t("notifications.work_order_regular_closed_success"))
      return closedWorkOrder
    }
  } catch (error) {
    showErrorNotification(t("sfxErrorMsg.error_closing_work_order_regular"))
    Log.error({
      message: "An error occurred during regular work order closing data operations",
      error,
    })
  }
}

export async function updateItemListInWorkOrderRegularItem(
  workOrderId: string,
  itemList: string[]
) {
  try {
    const data = await patchWorkOrderRegularItem({}, workOrderId, { items: itemList })

    const db = await getDB()
    await db.work_order_regular_item.upsert(data)

    return data
  } catch (error) {
    const { t } = i18n.global
    showErrorNotification(t("sfxErrorMsg.error_updating_regular_work_order"))
    Log.error({
      message: "An error occurred while updating the item list in RegularWorkOrderItem",
      error,
    })
  }
}
